import { useToast } from '@chakra-ui/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  previewSnapshotCsv,
  type SnapshotCsvFieldId,
  type SnapshotCsvPreview,
  type SnapshotStageBucketId,
} from 'services/snapshotsService';
import { getSnapshotApiErrorMessage } from './snapshotApiError';

export type ColumnMappingState = Partial<Record<SnapshotCsvFieldId, string>>;

const REQUIRED_FIELDS: SnapshotCsvFieldId[] = ['url', 'company', 'lifecycleStage'];

export function mappingToPayload(mapping: ColumnMappingState): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [fieldId, header] of Object.entries(mapping)) {
    if (header) out[fieldId] = header;
  }
  return out;
}

export function fieldForHeader(
  mapping: ColumnMappingState,
  header: string,
): SnapshotCsvFieldId | '' {
  for (const [fieldId, mappedHeader] of Object.entries(mapping)) {
    if (mappedHeader === header) return fieldId as SnapshotCsvFieldId;
  }
  return '';
}

export function assignHeaderToField(
  mapping: ColumnMappingState,
  header: string,
  fieldId: SnapshotCsvFieldId | '',
): ColumnMappingState {
  const next: ColumnMappingState = { ...mapping };
  for (const key of Object.keys(next) as SnapshotCsvFieldId[]) {
    if (next[key] === header) delete next[key];
  }
  if (fieldId) {
    for (const key of Object.keys(next) as SnapshotCsvFieldId[]) {
      if (key === fieldId) delete next[key];
    }
    next[fieldId] = header;
  }
  return next;
}

function mergeStageMapping(
  preview: SnapshotCsvPreview,
  prev: Record<string, SnapshotStageBucketId>,
): Record<string, SnapshotStageBucketId> {
  const next = { ...prev };
  for (const stage of preview.stages) {
    if (!next[stage.value]) {
      next[stage.value] =
        stage.suggestedBucket || preview.suggestedStageMapping[stage.value] || 'lead';
    }
  }
  for (const key of Object.keys(next)) {
    if (!preview.stages.some((s) => s.value === key)) {
      delete next[key];
    }
  }
  return next;
}

export function validateSnapshotCsvMappings(
  preview: SnapshotCsvPreview | null,
  columnMapping: ColumnMappingState,
  stageMapping: Record<string, SnapshotStageBucketId>,
) {
  if (!preview) {
    return { ok: false as const, reason: 'missing_preview' as const };
  }
  const missingRequired = REQUIRED_FIELDS.filter((id) => !columnMapping[id]);
  const unmappedStages = preview.stages.filter((s) => !stageMapping[s.value]);
  if (missingRequired.length > 0) {
    return { ok: false as const, reason: 'columns' as const, missingRequired };
  }
  if (unmappedStages.length > 0) {
    return { ok: false as const, reason: 'stages' as const, unmappedStages };
  }
  if (preview.rowImport && preview.rowImport.importableCount === 0) {
    return { ok: false as const, reason: 'no_rows' as const };
  }
  return {
    ok: true as const,
    columnMapping: mappingToPayload(columnMapping),
    stageMapping,
  };
}

export function useSnapshotCsvMapping(token: string | undefined, file: File | null) {
  const toast = useToast();
  const [preview, setPreview] = useState<SnapshotCsvPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [columnMapping, setColumnMapping] = useState<ColumnMappingState>({});
  const [stageMapping, setStageMapping] = useState<
    Record<string, SnapshotStageBucketId>
  >({});
  const lifecycleRefreshRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initializedRef = useRef(false);

  const fetchPreview = useCallback(
    async (
      columns: ColumnMappingState,
      stages: Record<string, SnapshotStageBucketId>,
    ) => {
      if (!token || !file) return null;
      setLoading(true);
      try {
        const columnPayload = mappingToPayload(columns);
        const stagePayload =
          Object.keys(stages).length > 0 ? stages : undefined;
        const data = await previewSnapshotCsv(
          token,
          file,
          Object.keys(columnPayload).length > 0 ? columnPayload : undefined,
          stagePayload,
        );
        setPreview(data);
        return data;
      } catch (err) {
        toast({
          title: getSnapshotApiErrorMessage(err, 'Could not read CSV'),
          status: 'error',
        });
        return null;
      } finally {
        setLoading(false);
      }
    },
    [file, token, toast],
  );

  useEffect(() => {
    initializedRef.current = false;
    if (!token || !file) {
      setPreview(null);
      setColumnMapping({});
      setStageMapping({});
      return;
    }

    fetchPreview({}, {}).then((data) => {
      if (!data) return;
      const cols = data.columnMapping as ColumnMappingState;
      const stages = mergeStageMapping(data, {});
      setColumnMapping(cols);
      setStageMapping(stages);
      initializedRef.current = true;
      fetchPreview(cols, stages);
    });

    return () => {
      if (lifecycleRefreshRef.current) clearTimeout(lifecycleRefreshRef.current);
    };
  }, [file, token, fetchPreview]);

  useEffect(() => {
    if (!initializedRef.current || !token || !file) return;
    const timer = setTimeout(() => {
      fetchPreview(columnMapping, stageMapping);
    }, 350);
    return () => clearTimeout(timer);
  }, [stageMapping, columnMapping, token, file, fetchPreview]);

  const assignColumn = (header: string, fieldId: SnapshotCsvFieldId | '') => {
    const next = assignHeaderToField(columnMapping, header, fieldId);
    setColumnMapping(next);
    if (fieldId === 'lifecycleStage' || next.lifecycleStage) {
      if (lifecycleRefreshRef.current) clearTimeout(lifecycleRefreshRef.current);
      lifecycleRefreshRef.current = setTimeout(() => {
        fetchPreview(next, stageMapping);
      }, 300);
    }
  };

  const reset = () => {
    initializedRef.current = false;
    setPreview(null);
    setColumnMapping({});
    setStageMapping({});
  };

  return {
    preview,
    loading,
    columnMapping,
    stageMapping,
    setStageMapping,
    assignColumn,
    reset,
    validate: () => validateSnapshotCsvMappings(preview, columnMapping, stageMapping),
  };
}
